import React, { use, useMemo } from "react";
import { FieldLabel, FieldLabelProps } from "@ui-components/FieldLabel";
import { FormattedMessage } from "react-intl";
import { Button, Flex, RefSelectProps, Tooltip } from "antd";

import {
    InputWithAutocomplete,
    InputWithAutocompleteProps,
} from "../baseComponents/InputWithAutocomplete/InputWithAutocomplete";
import { TableViewContext } from "../../../../../scripts/context/accountingTable/tableViewContext";
import { KontoContext, KontoControlContext } from "../../../../../scripts/context/accountingData/KontoEntitiesProvider";
import { GQL } from "@binale-tech/shared";
import { PlusOutlined } from "@ant-design/icons";
import { RefCallBack } from "react-hook-form";

const RecordTextTemplateSaveButton = React.memo(function SaveRecordTemplateButton({
    text,
    templatesList,
    type,
}: {
    templatesList: string[];
    text: string;
    type: GQL.ITextTemplateType;
}) {
    const { recordTextTemplateSave } = use(KontoControlContext);
    const { productKey, selectedRecordGroup } = use(TableViewContext);

    const templatesSet = useMemo(() => new Set(templatesList), [templatesList]);
    if (!text) {
        return null;
    }
    if (typeof text !== "string") {
        return null;
    }
    if (templatesSet.has(text)) {
        return null;
    }

    return (
        <Tooltip placement="top" title={<FormattedMessage id="app.button.save" />}>
            <Button
                shape="circle"
                icon={<PlusOutlined />}
                size="small"
                onClick={() =>
                    recordTextTemplateSave({
                        id: undefined,
                        text,
                        type,
                        productIds: [productKey as GQL.IProductKey],
                        kbIds: productKey === GQL.IProductKey.Kb && selectedRecordGroup ? [selectedRecordGroup] : [],
                        bankIds:
                            productKey === GQL.IProductKey.Bank && selectedRecordGroup ? [selectedRecordGroup] : [],
                    })
                }
                style={{
                    minWidth: 18,
                    width: 18,
                    height: 18,
                }}
            />
        </Tooltip>
    );
});

const getFilterPredicate =
    (type: GQL.ITextTemplateType, productKey: GQL.IProductKey, selectedRecordGroup?: string) =>
    (v: GQL.IGenericRecordTextTemplate) => {
        if (v.type !== type) {
            return false;
        }
        if (!v.productIds.includes(productKey)) {
            return false;
        }
        if (productKey === GQL.IProductKey.Kb) {
            return (v.kbIds ?? []).includes(selectedRecordGroup);
        }
        if (productKey === GQL.IProductKey.Bank) {
            return (v.bankIds ?? []).includes(selectedRecordGroup);
        }
        return true;
    };

interface BuchungstextInputBlockProps extends Omit<InputWithAutocompleteProps, "storageId" | "autocompleteList"> {
    labelProps?: FieldLabelProps;
}

export const BuchungstextInputBlock = function BuchungstextInputBlock({
    ref,
    labelProps,
    ...props
}: BuchungstextInputBlockProps & {
    ref?: React.RefObject<RefSelectProps> | RefCallBack;
}) {
    const { recordTextTemplates } = use(KontoContext);
    const { productKey, selectedRecordGroup } = use(TableViewContext);

    const autocompleteList = useMemo(() => {
        return recordTextTemplates
            .filter(getFilterPredicate(GQL.ITextTemplateType.Text1, productKey as GQL.IProductKey, selectedRecordGroup))
            .map(v => v.text);
    }, [productKey, recordTextTemplates, selectedRecordGroup]);

    return (
        <FieldLabel
            label={
                <Flex gap={8}>
                    <FormattedMessage id="app.fields.buchtext" />

                    <RecordTextTemplateSaveButton
                        text={props.value}
                        templatesList={autocompleteList}
                        type={GQL.ITextTemplateType.Text1}
                    />
                </Flex>
            }
            {...labelProps}
        >
            <InputWithAutocomplete
                ref={ref}
                {...props}
                inputProps={{ showCount: true, ...props.inputProps }}
                maxLength={60}
                selectTextOnFocus
                autocompleteList={autocompleteList}
            />
        </FieldLabel>
    );
};

export const Buchungstext2InputBlock = function Buchungstext2InputBlock({
    ref,
    labelProps,
    ...props
}: BuchungstextInputBlockProps & {
    ref?: React.RefObject<RefSelectProps> | RefCallBack;
}) {
    const { recordTextTemplates } = use(KontoContext);
    const { productKey, selectedRecordGroup } = use(TableViewContext);

    const autocompleteList = useMemo(() => {
        return recordTextTemplates
            .filter(getFilterPredicate(GQL.ITextTemplateType.Text2, productKey as GQL.IProductKey, selectedRecordGroup))
            .map(v => v.text);
    }, [productKey, recordTextTemplates, selectedRecordGroup]);

    return (
        <FieldLabel
            label={
                <Flex gap={8}>
                    <FormattedMessage id="app.fields.buchtext2" />

                    <RecordTextTemplateSaveButton
                        text={props.value}
                        templatesList={autocompleteList}
                        type={GQL.ITextTemplateType.Text2}
                    />
                </Flex>
            }
            {...labelProps}
        >
            <InputWithAutocomplete
                ref={ref}
                {...props}
                inputProps={{ showCount: true, ...props.inputProps }}
                maxLength={80}
                selectTextOnFocus
                autocompleteList={autocompleteList}
            />
        </FieldLabel>
    );
};
